<script lang="ts">
    import { onMount } from 'svelte/internal'
    import { slide } from 'svelte/transition'
    import Fa from 'svelte-fa'
    import {
        faAngleDoubleLeft,
        faAngleDoubleRight,
        faChartLine,
        faCoins,
        faUsers,
        faCalendarCheck,
        faSearch,
        faTimes,
        faHome,
        faSmile,
        faCubes,
    } from '@fortawesome/free-solid-svg-icons'
    import { Route, Link, Router } from 'yrv'
    import Content from '../_common/content.svelte'
    import Ztab from '../_common/z/ztab.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import ReportsV2Home from './reports-v2-home.svelte'
    import ReportGeneric from './report-generic.svelte'
    import FinancialEntriesByCategoryV2 from './financial/financial-entries-by-category-v2.svelte'
    import FinancialEntriesByCategoryDetailV2 from './financial/financial-entries-by-category-detail-v2.svelte'
    import FinancialBankEntriesV2 from './financial/financial-bank-entries-v2.svelte'
    import FinancialDreV2 from './financial/financial-dre-v2.svelte'
    import ScheduleTeamOccupancyRateV2 from './schedule/schedule-team-occupancy-rate-v2.svelte'
    import type { ReportCatalogItem } from '../../modules/reportsApi/catalog/report-catalog'
    import { canReportCategory } from '../../modules/infra/permissions'

    // Telas que ainda precisam de filtros específicos (não genéricos) entram aqui — qualquer relatório do
    // catálogo sem entrada nesse mapa cai automaticamente no ReportGeneric (filtros simples + grid genérico).
    const CUSTOM_COMPONENTS: Record<string, any> = {
        'financial-entries-by-category': FinancialEntriesByCategoryV2,
        'financial-entries-by-category-detail': FinancialEntriesByCategoryDetailV2,
        'financial-bank-entries': FinancialBankEntriesV2,
        'financial-dre': FinancialDreV2,
        'schedule-team-occupancy-rate': ScheduleTeamOccupancyRateV2,
    }

    const CATEGORY_ICONS: Record<string, any> = {
        Vendas: faCoins,
        Pessoas: faUsers,
        'Agenda e atendimento': faCalendarCheck,
        NPS: faSmile,
        Estoque: faCubes,
    }
    function categoryIcon(category: string) {
        return CATEGORY_ICONS[category] || faChartLine
    }

    let show = false
    let reports: ReportCatalogItem[] = []
    let categories: string[] = []
    let search = ''

    onMount(async () => {
        try {
            show = true
            // só as categorias liberadas: some do menu, da home e das rotas (link direto não abre)
            reports = (await window.api.reports.catalog.getAll()).filter((el) => canReportCategory(el.category))
            categories = [...new Set(reports.map((el) => el.category))]
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    })

    $: filteredReports = reports.filter((el) => !search || el.label.toLowerCase().includes(search.toLowerCase()))

    let showList = true
    function toggleList() {
        showList = !showList
    }

    // em telas menores a lista atrapalha o espaço do relatório — some sozinha ao escolher um. Home mantém aberta.
    function onReportClick() {
        if (window.innerWidth <= 1600) showList = false
    }

    let tabs = []
    tabs.push({ title: 'Relatórios', url: '/reports-v2/home' })
</script>

<Content maxWidth="1800px">
    <div class="rv2 m-bottom-sm">
        <div class="rv2-body mt-3 mb-2">
            <aside class="rv2-sidebar h-print" class:rv2-sidebar_collapsed={!showList}>
                {#if showList}
                    <div class="rv2-sidebar_inner scroll-dark" transition:slide={{ duration: 200, axis: 'x' }}>
                        <div class="rv2-search">
                            <Fa icon={faSearch} class="rv2-search_icon" />
                            <input type="text" placeholder="Buscar relatório..." bind:value={search} />
                            {#if search}
                                <button class="rv2-search_clear" on:click={() => (search = '')}><Fa icon={faTimes} /></button>
                            {/if}
                        </div>

                        <Link class="rv2-report rv2-report_home" href="/reports-v2/home">
                            <Fa icon={faHome} />
                            <div class="rv2-report_label">Home</div>
                        </Link>

                        {#each categories as category}
                            {@const items = filteredReports.filter((el) => el.category === category)}
                            {#if items.length}
                                <div class="rv2-category">
                                    <div class="rv2-category_title">
                                        <Fa icon={categoryIcon(category)} />
                                        <div>{category}</div>
                                    </div>
                                    {#each items as report}
                                        <Link class="rv2-report" href="/reports-v2/{report.id}" on:click={onReportClick}>
                                            <span class="rv2-report_label">{report.label}</span>
                                        </Link>
                                    {/each}
                                </div>
                            {/if}
                        {/each}

                        {#if !show && !reports.length}
                            <div class="rv2-empty">Nenhum relatório disponível.</div>
                        {/if}
                    </div>
                {/if}

                <button class="rv2-collapse" on:click={toggleList} title={showList ? 'Recolher menu' : 'Expandir menu'}>
                    <Fa icon={showList ? faAngleDoubleLeft : faAngleDoubleRight} />
                </button>
            </aside>

            <main class="rv2-content scroll-dark pe-2">
                <Router>
                    <Route path="/home">
                        <ReportsV2Home {reports} {categories} loading={show} />
                    </Route>
                    {#each reports as report}
                        <Route path="/{report.id}">
                            <svelte:component this={CUSTOM_COMPONENTS[report.id] || ReportGeneric} {report} />
                        </Route>
                    {/each}
                </Router>
            </main>
        </div>
    </div>
    <LoadSpinner {show}></LoadSpinner>
</Content>

<style>
    .rv2 {
        height: 100%;
        display: flex;
        flex-direction: column;
        padding: 8px 0px 8px 22px;
        padding-top: 0px;
    }

    .rv2-body {
        flex: 1 1 auto;
        display: flex;
        gap: 12px;
        min-height: 0;
    }

    /* ===== sidebar ===== */
    .rv2-sidebar {
        flex: 0 0 360px;
        display: flex;

        border-radius: 10px;
        overflow: hidden;
        background: rgba(150, 150, 150, 0.08);
        border: 1px solid rgba(150, 150, 150, 0.12);
        transition: flex-basis 0.2s ease;
    }
    .rv2-sidebar_collapsed {
        flex: 0 0 32px;
    }
    .rv2-sidebar_inner {
        flex-grow: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 10px;
    }

    .rv2-search {
        position: relative;
        display: flex;
        align-items: center;
        margin-bottom: 12px;
    }
    .rv2-search :global(.rv2-search_icon) {
        position: absolute;
        left: 10px;
        opacity: 0.5;
        font-size: 13px;
        pointer-events: none;
    }
    .rv2-search input {
        width: 100%;
        background: rgba(150, 150, 150, 0.12);
        border: 1px solid rgba(150, 150, 150, 0.18);
        border-radius: 8px;
        padding: 7px 30px;
        font-size: 14px;
        color: inherit;
    }
    .rv2-search input:focus {
        outline: none;
        border-color: rgb(var(--menu-color));
    }
    .rv2-search_clear {
        position: absolute;
        right: 6px;
        background: none;
        border: none;
        opacity: 0.6;
        padding: 4px 6px;
        cursor: pointer;
    }
    .rv2-search_clear:hover {
        opacity: 1;
    }

    .rv2-category {
        margin-bottom: 14px;
    }
    .rv2-category_title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        opacity: 0.55;
        padding: 4px 0px;
        margin-bottom: 2px;
    }

    .rv2-category_title div {
        display: flex;
        align-items: center;
        padding-top: 2px;
    }

    :global(.rv2-category_title svg) {
        font-size: 22px;
        padding: 4px;
        border: 1px solid #88888899;
        border-radius: 2px;
    }

    .rv2-sidebar :global(a.rv2-report) {
        display: block;
        padding: 4px 10px 4px 26px;
        margin: 1px 0;
        border-radius: 7px;
        font-size: 14px;
        opacity: 0.85;
        border-left: 2px solid transparent;
        transition:
            background-color 0.12s ease,
            opacity 0.12s ease;
    }
    .rv2-sidebar :global(a.rv2-report:hover) {
        background: rgba(150, 150, 150, 0.14);
        opacity: 1;
    }
    .rv2-sidebar :global(a.rv2-report[aria-current='page']) {
        background: rgba(var(--menu-color), 0.16);
        border-left-color: rgb(var(--menu-color));
        opacity: 1;
        font-weight: 600;
    }

    .rv2-sidebar :global(a.rv2-report_home) {
        display: flex;
        align-items: center;
        gap: 8px;
        padding-left: 0px;
        font-weight: 600;
    }

    .rv2-sidebar :global(.rv2-report_home div) {
        padding-top: 3px;
    }

    .rv2-empty {
        padding: 12px 8px;
        font-size: 13px;
        opacity: 0.6;
        text-align: center;
    }

    .rv2-collapse {
        flex: 0 0 24px;
        border: none;
        border-left: 1px solid rgba(150, 150, 150, 0.15);
        background: rgba(150, 150, 150, 0.05);
        opacity: 0.6;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .rv2-collapse:hover {
        opacity: 1;
        background: rgba(150, 150, 150, 0.12);
    }

    /* ===== content ===== */
    .rv2-content {
        flex: 1 1 auto;
        min-width: 0;
        max-width: 100%;
        overflow-x: hidden;
        padding-bottom: 100px;
    }
</style>
